import { RefObject, useEffect, useRef } from 'react';

const ROOT_MARGIN = '120px';

interface UseInfiniteScrollObserverParams {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  rootRef: RefObject<HTMLElement | null>;
}

const useInfiniteScrollObserver = ({
  hasMore,
  isLoading,
  onLoadMore,
  rootRef,
}: UseInfiniteScrollObserverParams) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(isLoading);
  const onLoadMoreRef = useRef(onLoadMore);

  isLoadingRef.current = isLoading;
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingRef.current) {
          onLoadMoreRef.current();
        }
      },
      { root: rootRef.current, rootMargin: ROOT_MARGIN },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, rootRef]);

  return sentinelRef;
};

export default useInfiniteScrollObserver;
